import { Box } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import { getModuleTypeName } from "Forms/Utils/EnumOptions";
import type { ModuleListItemDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { ModuleStatusChip } from "Modules/Components/ModuleStatusChip";
import { MODULE_TYPE_CONFIG } from "Modules/Utils/moduleTypeConfig";
import { DetailActionCell } from "Shared/DataTable/DetailActionCell";
import { NameLinkCell } from "Shared/NameLinkCell";
import { formatDate } from "Shared/Utils/Format/date";

const columnHelper = createColumnHelper<ModuleListItemDto>();

export const MODULES_SORTABLE_COLUMNS = [
  "name",
  "type",
  "projectCount",
] as const;

const isSortable = (id: string) =>
  (MODULES_SORTABLE_COLUMNS as readonly string[]).includes(id);

export function useModulesTableColumns() {
  const { t } = useResource();

  return useMemo(
    () => [
      columnHelper.accessor("name", {
        header: t(Resources.Modules.ModuleName),
        enableSorting: isSortable("name"),
        cell: (info) => {
          const config = MODULE_TYPE_CONFIG[info.row.original.type];
          return (
            <NameLinkCell
              path={AppRouting.ModuleDetail.path(info.row.original.moduleID)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    color: config.color,
                    "& svg": { fontSize: 18 },
                  }}
                >
                  {config.icon}
                </Box>
                {info.getValue()}
              </Box>
            </NameLinkCell>
          );
        },
      }),
      columnHelper.accessor("type", {
        header: t(Resources.Modules.ModuleBase),
        enableSorting: isSortable("type"),
        cell: (info) => getModuleTypeName(info.getValue()),
      }),
      columnHelper.accessor("projectCount", {
        header: t(Resources.Modules.Projects),
        enableSorting: isSortable("projectCount"),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("dateUpdated", {
        header: t(Resources.Modules.LastUpdated),
        enableSorting: isSortable("dateUpdated"),
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor("status", {
        header: t(Resources.Modules.Availability),
        enableSorting: isSortable("status"),
        cell: (info) => <ModuleStatusChip status={info.getValue()} />,
      }),
      columnHelper.display({
        id: "action",
        header: "",
        enableSorting: false,
        meta: { maxWidth: 50, justifySelf: "end" },
        cell: (info) => (
          <DetailActionCell
            path={AppRouting.ModuleDetail.path(info.row.original.moduleID)}
          />
        ),
      }),
    ],
    [t],
  );
}
